import AnalyticsWebClient, { envType, tenantType, userType } from '@atlassiansox/analytics-web-client';
import { AppNames, getAppName, getConfig, getEnv } from '@atlassian/teams-common/context-config';
import { payloadPublisher } from '@atlassian/ufo';
import { analyticsKeyToEventMap } from '../components/AnalyticsContext';
import { configureNewTracker } from '../monitor/experience-tracker';
import referrerInfo from '../utils/referrerInfo';
function getServerName() {
  return window.location.hostname;
}
const {
  LOCAL,
  DEV,
  STAGING,
  PROD
} = envType;
const ENV_MAP = {
  local: LOCAL,
  test: LOCAL,
  adev: DEV,
  'stg-east': STAGING,
  stg: STAGING
};
const getEnvType = env => {
  return ENV_MAP[env] || PROD;
};
const analyticsConfig = getConfig().analytics;
const SUB_PRODUCT_DEFAULT = 'siteDirectory';
const PRODUCT_DEFAULT = 'people';

// tslint:disable-line no-any

class AnalyticsClient {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any

  constructor(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  options = {}, {
    commonProperties = {},
    eventPrefix = 'atlassian.people',
    subproduct = SUB_PRODUCT_DEFAULT,
    cloudId = ''
  } = {}) {
    const customOptions = {
      commonProperties,
      eventPrefix
    };
    this.cloudId = cloudId;
    this.commonProperties = customOptions.commonProperties;
    this.eventPrefix = customOptions.eventPrefix;
    this.state = {
      analyticsscheme: analyticsConfig.scheme,
      analyticsserver: analyticsConfig.server,
      server: getServerName(),
      product: PRODUCT_DEFAULT,
      isReadyToSend: false,
      pendingEventsQueue: [],
      storage_key: 'atlassian.people.analytics',
      ...options
    };

    // New GAS pipeline client
    this.analyticsWebClient = new AnalyticsWebClient({
      env: getEnvType(getEnv()),
      product: 'people',
      subproduct
    }, {});
    this.initUFO();
    this._expTracker = configureNewTracker(this);
  }
  initUFO() {
    if (getAppName() !== AppNames.STANDALONE_DIRECTORY) {
      return;
    }
    payloadPublisher.setup({
      gasv3: this.analyticsWebClient,
      product: 'people',
      app: {
        // @ts-ignore
        version: {
          web: __VERSION__
        }
      },
      region: getEnv()
    });

    // import { ufologger } from @atlassian/ufo and uncomment the following line to see UFO logs:
    // ufologger.enable();
  }
  getAnalyticsWebClient() {
    return this.analyticsWebClient;
  }
  expTracker() {
    return this._expTracker;
  }
  sendTrackEvent(eventData) {
    this.analyticsWebClient.sendTrackEvent(eventData);
  }

  /**
   * Checks whether the library has been initialized and decides whether the event should be sent or cached
   * @param {*} eventType Values could be 'track', 'screen', 'operation, 'ui'
   * @param {*} eventData Data of the event
   * @param {Function} cb Callback is called when the event is sent successfully.
   */
  canEventBeSent(eventType, eventData, cb) {
    if (this.state.isReadyToSend) {
      return true;
    }

    // add to `pendingEventsQueue` in order to send the event later
    this.state.pendingEventsQueue.push({
      eventType,
      eventData,
      // `eventCallback` is called when `pendingEventsQueue` is processed and eventData is sent
      eventCallback: cb
    });
    return false;
  }
  pushTrackEvent(eventData, cb) {
    if (this.canEventBeSent('track', eventData, cb)) {
      const event = this.buildEventPayload(eventData);
      this.analyticsWebClient.sendTrackEvent(event, cb);
    }
  }
  pushUIEvent(eventData, cb) {
    if (this.canEventBeSent('ui', eventData, cb)) {
      const event = this.buildEventPayload(eventData);
      this.analyticsWebClient.sendUIEvent(event, cb);
    }
  }
  pushOperationalEvent(eventData, cb) {
    if (this.canEventBeSent('operation', eventData, cb)) {
      const event = this.buildEventPayload(eventData);
      this.analyticsWebClient.sendOperationalEvent(event, cb);
    }
  }
  pushScreenEvent(eventData, cb) {
    const additionalAttributes = referrerInfo.get();
    referrerInfo.markUsed();
    if (eventData && additionalAttributes) {
      eventData = {
        ...eventData,
        attributes: {
          ...additionalAttributes,
          ...eventData.attributes
        }
      };
    }
    if (this.canEventBeSent('screen', eventData, cb)) {
      this.analyticsWebClient.sendScreenEvent(eventData, cb);
    }
  }
  buildEventPayload(eventData) {
    return {
      containerType: null,
      containerId: null,
      objectType: null,
      objectId: null,
      source: null,
      actionSubject: null,
      action: null,
      actionSubjectId: null,
      attributes: null,
      tags: [],
      ...eventData
    };
  }
  mapAnalyticsKeyToEvent(analyticsKey) {
    return analyticsKeyToEventMap[analyticsKey] || null;
  }

  /**
   *  Start up E-MAU events in the new analytics pipeline (GAS v3)
   *  See: https://extranet.atlassian.com/display/BPO/DACI%3A+E-MAU+Definition
   */
  async startUIViewedEvent(principalUserId) {
    if (!principalUserId) {
      return;
    }
    this.analyticsWebClient.setTenantInfo(tenantType.CLOUD_ID, this.cloudId || '');
    this.analyticsWebClient.setUserInfo(userType.ATLASSIAN_ACCOUNT, principalUserId);
    this.analyticsWebClient.startUIViewedEvent();
    this.state.isReadyToSend = true;
    this.flushPendingEventsQueue();
  }

  /**
   * Flush all the pending events in the queue
   */
  flushPendingEventsQueue() {
    this.state.pendingEventsQueue.forEach(element => {
      const {
        eventType,
        eventData,
        eventCallback
      } = element;
      switch (eventType) {
        case 'track':
          this.pushTrackEvent(eventData, eventCallback);
          break;
        case 'ui':
          this.pushUIEvent(eventData, eventCallback);
          break;
        case 'screen':
          this.pushScreenEvent(eventData, eventCallback);
          break;
        case 'operation':
          this.pushOperationalEvent(eventData, eventCallback);
          break;
        default:
          // eslint-disable-next-line no-console
          console.error('Invalide Event :', element);
          break;
      }
    });
  }
}
export default AnalyticsClient;
let preservedAnaylticsClient;

/**
 * Cloud Id is a required data to create analytics client instance.
 * The instance will be cached and reused when `getAnalyticsClient` is called multiple times.
 * @param mode
 * @param subproduct
 * @param cloudId
 */
export const getAnalyticsClient = ({
  mode,
  subproduct,
  cloudId
}) => {
  if (!cloudId) {
    return;
  }
  if (preservedAnaylticsClient) {
    return preservedAnaylticsClient;
  }
  preservedAnaylticsClient = new AnalyticsClient({}, {
    commonProperties: {
      mode
    },
    subproduct,
    cloudId
  });
  return preservedAnaylticsClient;
};